/**
 * App settings — display name, notifications, theme, account actions.
 * Intentionally slim: clinical profile data lives at /patient/profile
 * (fed into pattern detection, cycle predictor, etc.), not here.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'motion/react'
import { useClerk } from '@/hooks/useClerk'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useProfile, useSaveProfile } from '@pelvia/shared/hooks/patient/useProfile'
import { useWearableStatus } from '@pelvia/shared/hooks/patient/useWearables'
import { api } from '@/lib/api'

const SPRING = { type: 'spring' as const, stiffness: 300, damping: 26 }

export default function SettingsPage() {
  const { data: user } = useCurrentUser()
  const { data: profileResp } = useProfile()
  const saveProfile = useSaveProfile()
  const { clerk } = useClerk()

  const [displayName, setDisplayName] = useState('')
  const [initialName, setInitialName] = useState('')

  // Seed the display-name input from server data once loaded.
  if (profileResp?.profile && initialName === '' && displayName === '') {
    const seeded = profileResp.profile.display_name || user?.full_name || ''
    if (seeded) {
      setDisplayName(seeded)
      setInitialName(seeded)
    }
  }

  const dirty = displayName.trim() !== initialName.trim()
  const saveName = async () => {
    await saveProfile.mutateAsync({ display_name: displayName.trim() || null })
    setInitialName(displayName)
  }

  return (
    <div className="settings-page">
      <header className="profile-header">
        <h1>App settings</h1>
        <p>
          Looking for diagnoses, cycle, and medications?{' '}
          <a href="/patient/profile">Your health profile →</a>
        </p>
      </header>

      <section className="card">
        <div className="card-header">
          <span className="card-title">Account</span>
        </div>
        <div className="form-field">
          <label className="form-label">Display name</label>
          <div className="settings-inline-input">
            <input
              className="form-input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How should we greet you?"
            />
            <motion.button
              type="button"
              className="btn btn-primary"
              onClick={saveName}
              disabled={!dirty || saveProfile.isPending}
              whileTap={{ scale: 0.96 }}
            >
              {saveProfile.isPending ? 'Saving…' : 'Save'}
            </motion.button>
          </div>
        </div>
        <div className="form-field">
          <label className="form-label">Email</label>
          <input className="form-input" value={user?.email || ''} readOnly disabled />
        </div>
      </section>

      <ThemeCard />

      <NotificationsCard />

      <WearablesCard />

      <AccountActionsCard onSignOut={async () => { await clerk?.signOut?.() }} />
    </div>
  )
}

function ThemeCard() {
  return (
    <section className="card">
      <div className="card-header">
        <span className="card-title">Theme</span>
        <span className="card-header-sub">Coming soon</span>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', margin: 0 }}>
        Dark mode is on the way — we'll light it up here once it's ready.
      </p>
    </section>
  )
}

function NotificationsCard() {
  // Notification prefs aren't wired to a backend yet — stored client-side so
  // the UI is real, and the server plumbing can catch up later.
  const [daily, setDaily] = useState<boolean>(() => storedPref('notif-daily', true))
  const [weekly, setWeekly] = useState<boolean>(() => storedPref('notif-weekly', true))
  const [cycleAlerts, setCycle] = useState<boolean>(() => storedPref('notif-cycle', true))

  const toggle = (key: string, next: boolean, setter: (v: boolean) => void) => {
    setter(next)
    localStorage.setItem(key, next ? '1' : '0')
  }

  return (
    <section className="card">
      <div className="card-header">
        <span className="card-title">Notifications</span>
        <span className="card-header-sub">Email preferences</span>
      </div>
      <ToggleRow
        label="Daily check-in reminder"
        hint="A gentle nudge each evening if you haven’t logged."
        checked={daily}
        onChange={(v) => toggle('notif-daily', v, setDaily)}
      />
      <ToggleRow
        label="Weekly summary"
        hint="Your week at a glance, sent every Sunday."
        checked={weekly}
        onChange={(v) => toggle('notif-weekly', v, setWeekly)}
      />
      <ToggleRow
        label="Cycle alerts"
        hint="Heads-up before your period or ovulation window."
        checked={cycleAlerts}
        onChange={(v) => toggle('notif-cycle', v, setCycle)}
      />
    </section>
  )
}

function WearablesCard() {
  const { data, isLoading } = useWearableStatus()
  const integrations = data?.integrations || []
  // Surface the first active/pending/error integration first; fall back to any.
  const priority = {
    active: 0, syncing: 1, pending: 2, error: 3, needs_reauth: 3, revoked: 4,
  } as const
  const primary = [...integrations].sort(
    (a, b) => (priority[a.status] ?? 9) - (priority[b.status] ?? 9),
  )[0]

  const label = (() => {
    if (isLoading) return { text: 'Checking connection…', tone: 'neutral' as const }
    if (!primary || primary.status === 'revoked') return { text: 'Not connected', tone: 'neutral' as const }
    if (primary.status === 'active') return { text: 'Connected', tone: 'ok' as const }
    if (primary.status === 'syncing') return { text: 'Syncing…', tone: 'warn' as const }
    if (primary.status === 'pending') return { text: 'Awaiting authorization', tone: 'warn' as const }
    if (primary.status === 'error' || primary.status === 'needs_reauth') {
      return { text: 'Needs reconnect', tone: 'err' as const }
    }
    return { text: '—', tone: 'neutral' as const }
  })()

  const lastSync = primary?.last_sync_at ? formatLastSync(primary.last_sync_at) : null
  // Use the server-resolved display label (e.g. "Apple Health", "Oura Ring")
  // rather than uppercasing the raw slug, which would render "APPLE_HEALTH".
  const providerName = primary?.provider_display
    || (primary?.provider ? primary.provider.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : null)

  return (
    <section className="card">
      <div className="card-header">
        <span className="card-title">Wearables</span>
        <span className="card-header-sub">Oura, Whoop &amp; more</span>
      </div>
      <div className="settings-action">
        <div>
          <div className="settings-action-label">
            {providerName ? `${providerName} · ` : ''}
            <span className={`wearable-status-pill tone-${label.tone}`}>{label.text}</span>
          </div>
          <div className="settings-action-hint">
            {providerName
              ? `Sleep, HRV, and activity improve your pain predictions. Last sync ${lastSync ?? '—'}.`
              : 'Connect Oura, Whoop, or another device to auto-fill sleep, HRV, and activity into your daily check-ins.'}
          </div>
        </div>
        <Link to="/patient/integrations" className="btn btn-secondary">
          {primary && primary.status !== 'revoked' ? 'Manage wearables' : 'Connect a wearable'}
        </Link>
      </div>
    </section>
  )
}

function formatLastSync(iso: string): string {
  const then = new Date(iso)
  const diffMin = Math.round((Date.now() - then.getTime()) / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const h = Math.round(diffMin / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.round(h / 24)
  return `${d}d ago`
}

function storedPref(key: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback
  const v = localStorage.getItem(key)
  if (v == null) return fallback
  return v === '1'
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="toggle-row">
      <div className="toggle-body">
        <div className="toggle-label">{label}</div>
        {hint && <div className="toggle-hint">{hint}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        className={`toggle-switch${checked ? ' on' : ''}`}
        onClick={() => onChange(!checked)}
      >
        <motion.span
          className="toggle-knob"
          layout
          transition={SPRING}
          animate={{ x: checked ? 20 : 0 }}
        />
      </button>
    </div>
  )
}

function AccountActionsCard({ onSignOut }: { onSignOut: () => void }) {
  const [showDelete, setShowDelete] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const replay = async () => {
    try {
      await api.post('/api/patient/replay-onboarding')
      window.location.href = '/patient'
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const exportData = async () => {
    try {
      const res = await fetch('/api/account/export', { credentials: 'include' })
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `pelvia-export-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      setError((e as Error).message)
    }
  }

  const deleteAccount = async () => {
    if (confirmText !== 'DELETE') return
    setBusy(true)
    try {
      await api.post('/api/account/delete', { confirmation: 'DELETE' })
      await onSignOut()
      window.location.href = '/'
    } catch (e) {
      setError((e as Error).message)
      setBusy(false)
    }
  }

  return (
    <>
      <section className="card">
        <div className="card-header">
          <span className="card-title">App</span>
        </div>
        <div className="settings-action">
          <div>
            <div className="settings-action-label">Replay feature tour</div>
            <div className="settings-action-hint">Walk through Pelvia’s features again.</div>
          </div>
          <motion.button
            type="button"
            className="btn btn-secondary"
            onClick={replay}
            whileTap={{ scale: 0.96 }}
          >
            Replay
          </motion.button>
        </div>
        <div className="settings-action">
          <div>
            <div className="settings-action-label">Export my data</div>
            <div className="settings-action-hint">
              Download your logs, conversations, medications, and tracking history.
            </div>
          </div>
          <motion.button
            type="button"
            className="btn btn-secondary"
            onClick={exportData}
            whileTap={{ scale: 0.96 }}
          >
            Export
          </motion.button>
        </div>
      </section>

      <section className="card card-danger">
        <div className="card-header">
          <span className="card-title">Danger zone</span>
        </div>
        <div className="settings-action">
          <div>
            <div className="settings-action-label">Delete account</div>
            <div className="settings-action-hint">
              Permanently remove your account and all data. This cannot be undone.
            </div>
          </div>
          <motion.button
            type="button"
            className="btn btn-danger"
            onClick={() => setShowDelete(true)}
            whileTap={{ scale: 0.96 }}
          >
            Delete
          </motion.button>
        </div>
      </section>

      <AnimatePresence>
        {showDelete && (
          <motion.div
            className="modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => !busy && setShowDelete(false)}
          >
            <motion.div
              className="modal-panel"
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 20, opacity: 0, scale: 0.96 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.96 }}
              transition={SPRING}
            >
              <h3>Delete account</h3>
              <p>
                This will permanently delete your account, conversations, health data, and
                tracking history. This cannot be undone.
              </p>
              <div className="form-field">
                <label className="form-label">Type DELETE to confirm</label>
                <input
                  className="form-input"
                  placeholder="DELETE"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowDelete(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={deleteAccount}
                  disabled={busy || confirmText !== 'DELETE'}
                >
                  {busy ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {error && <div className="inline-error" role="alert">{error}</div>}
    </>
  )
}
